import React, { useState } from 'react';
import { FileIcon, DownloadIcon, ExternalLinkIcon, TrashIcon, FileSymlinkIcon, FolderIcon, CalendarIcon, UserIcon, TagIcon, LinkIcon, FolderTreeIcon, DatabaseIcon, ShieldIcon, ClockIcon, EditIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { formatDate, formatDateTime } from '../utils/dateFormatter';
export function FileDetails({
  file,
  onClose,
  onDownload,
  onMove,
  formatFileSize,
  locale = 'en-US'
}) {
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'relationships', 'history'
  const [copied, setCopied] = useState(false);
  // Generate a realistic nanoid-like string
  const generateNanoid = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 21; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };
  // Create a deterministic nanoid-like string based on the file ID
  const getNanoid = fileId => {
    // Use the file ID to seed a deterministic but nanoid-like string
    const seed = fileId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 21; i++) {
      // Use a deterministic approach to generate characters based on the seed
      const charIndex = seed * (i + 1) % chars.length;
      result += chars.charAt(Math.abs(charIndex));
    }
    return result;
  };
  // Generate file URL with the new format and proper nanoid
  const fileUrl = `https://demo.rapidp2p.io/files/${getNanoid(file.id)}`;
  // Handle copy to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(fileUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  // Get file type icon
  const getFileTypeIcon = fileType => {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return <FileIcon className="h-5 w-5 text-red-500" data-id="element-5028" />;
      case 'docx':
      case 'doc':
        return <FileIcon className="h-5 w-5 text-blue-500" data-id="element-5029" />;
      case 'xlsx':
      case 'xls':
        return <FileIcon className="h-5 w-5 text-green-500" data-id="element-5030" />;
      case 'pptx':
      case 'ppt':
        return <FileIcon className="h-5 w-5 text-orange-500" data-id="element-5031" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <FileIcon className="h-5 w-5 text-purple-500" data-id="element-5032" />;
      case 'zip':
      case 'rar':
        return <FileIcon className="h-5 w-5 text-gray-500" data-id="element-5033" />;
      default:
        return <FileIcon className="h-5 w-5 text-gray-500" data-id="element-5034" />;
    }
  };
  return <div className="h-full flex flex-col" data-id="element-5035">
      {/* File Header */}
      <div className="border-b border-gray-200 pb-4" data-id="element-5036">
        <div className="flex items-center" data-id="element-5037">
          {getFileTypeIcon(file.type)}
          <h3 className="ml-2 text-lg font-medium text-gray-900 truncate" data-id="element-5038">
            {file.name}
          </h3>
        </div>
        <div className="mt-2 flex items-center text-sm text-gray-500" data-id="element-5039">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-2" data-id="element-5040">
            {file.type.toUpperCase()}
          </span>
          <span data-id="element-5041">{formatFileSize(file.size)}</span>
        </div>
        {/* File URL Section */}
        <div className="mt-3 flex items-center" data-id="element-5042">
          <div className="flex-1 relative" data-id="element-5043">
            <div className="flex items-center bg-gray-50 rounded-md p-2 pr-10 overflow-hidden" data-id="element-5044">
              <LinkIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mr-1.5" data-id="element-5045" />
              <span className="text-xs text-gray-600 truncate" data-id="element-5046">{fileUrl}</span>
            </div>
            <button onClick={copyToClipboard} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-indigo-600" title="Copy URL" data-id="element-5047">
              {copied ? <CheckIcon className="h-4 w-4 text-green-500" data-id="element-5048" /> : <CopyIcon className="h-4 w-4" data-id="element-5049" />}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-4 border-b border-gray-200" data-id="element-5050">
        <nav className="-mb-px flex space-x-8" data-id="element-5051">
          <button onClick={() => setActiveTab('details')} className={`pb-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'details' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5052">
            Details
          </button>
          <button onClick={() => setActiveTab('relationships')} className={`pb-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'relationships' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5053">
            Relationships
          </button>
          <button onClick={() => setActiveTab('history')} className={`pb-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'history' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5054">
            History
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-4 flex-1 overflow-y-auto" data-id="element-5055">
        {activeTab === 'details' && <div className="space-y-4" data-id="element-5056">
            {/* File Info */}
            <div data-id="element-5057">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5058">
                File Information
              </h4>
              <dl className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5059">
                <div className="py-3 flex justify-between text-sm" data-id="element-5060">
                  <dt className="text-gray-500" data-id="element-5061">File Name</dt>
                  <dd className="text-gray-900 truncate max-w-[200px]" data-id="element-5062">
                    {file.name}
                  </dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5063">
                  <dt className="text-gray-500" data-id="element-5064">File Type</dt>
                  <dd className="text-gray-900" data-id="element-5065">{file.type.toUpperCase()}</dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5066">
                  <dt className="text-gray-500" data-id="element-5067">Size</dt>
                  <dd className="text-gray-900" data-id="element-5068">{formatFileSize(file.size)}</dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5069">
                  <dt className="text-gray-500" data-id="element-5070">Department</dt>
                  <dd className="text-gray-900" data-id="element-5071">{file.department || 'N/A'}</dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5072">
                  <dt className="text-gray-500" data-id="element-5073">Status</dt>
                  <dd className="text-gray-900" data-id="element-5074">{file.status || 'Active'}</dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5075">
                  <dt className="text-gray-500" data-id="element-5076">File ID</dt>
                  <dd className="text-gray-900 font-mono" data-id="element-5077">{file.id}</dd>
                </div>
              </dl>
            </div>

            {/* Dates */}
            <div data-id="element-5078">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5079">Dates</h4>
              <dl className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5080">
                <div className="py-3 flex justify-between text-sm" data-id="element-5081">
                  <dt className="text-gray-500" data-id="element-5082">Upload Date</dt>
                  <dd className="text-gray-900" data-id="element-5083">
                    {formatDateTime(file.uploadDate, locale)}
                  </dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5084">
                  <dt className="text-gray-500" data-id="element-5085">Last Modified</dt>
                  <dd className="text-gray-900" data-id="element-5086">
                    {formatDateTime(file.lastModified, locale)}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Tags */}
            {file.tags && file.tags.length > 0 && <div data-id="element-5087">
                <h4 className="text-sm font-medium text-gray-900" data-id="element-5088">Tags</h4>
                <div className="mt-2 flex flex-wrap gap-2" data-id="element-5089">
                  {file.tags.map(tag => <span key={tag} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-5090">
                      <TagIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-5091" />
                      {tag}
                    </span>)}
                </div>
              </div>}

            {/* Storage Information */}
            <div data-id="element-5092">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5093">
                Storage Information
              </h4>
              <dl className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5094">
                <div className="py-3 flex justify-between text-sm" data-id="element-5095">
                  <dt className="text-gray-500" data-id="element-5096">Location</dt>
                  <dd className="text-gray-900 truncate max-w-[200px]" title={file.storageLocation} data-id="element-5097">
                    {file.storageLocation}
                  </dd>
                </div>
                <div className="py-3 flex justify-between text-sm" data-id="element-5098">
                  <dt className="text-gray-500" data-id="element-5099">Access Level</dt>
                  <dd className="text-gray-900" data-id="element-5100">{file.accessLevel}</dd>
                </div>
              </dl>
            </div>
          </div>}

        {activeTab === 'relationships' && <div className="space-y-4" data-id="element-5101">
            {/* Parent Files */}
            <div data-id="element-5102">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5103">
                Parent Files
              </h4>
              {file.parentFiles && file.parentFiles.length > 0 ? <ul className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5104">
                  {file.parentFiles.map(parent => <li key={parent.id} className="py-3 flex items-center justify-between text-sm" data-id="element-5105">
                      <div className="flex items-center" data-id="element-5106">
                        <FolderTreeIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-5107" />
                        <span className="text-gray-900" data-id="element-5108">{parent.name}</span>
                      </div>
                      <button className="text-indigo-600 hover:text-indigo-900" data-id="element-5109">
                        <ExternalLinkIcon className="h-4 w-4" data-id="element-5110" />
                      </button>
                    </li>)}
                </ul> : <p className="mt-2 text-sm text-gray-500 italic" data-id="element-5111">
                  No parent files
                </p>}
            </div>

            {/* Child Files */}
            <div data-id="element-5112">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5113">Child Files</h4>
              {file.childFiles && file.childFiles.length > 0 ? <ul className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5114">
                  {file.childFiles.map(child => <li key={child.id} className="py-3 flex items-center justify-between text-sm" data-id="element-5115">
                      <div className="flex items-center" data-id="element-5116">
                        <FileSymlinkIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-5117" />
                        <span className="text-gray-900" data-id="element-5118">{child.name}</span>
                      </div>
                      <button className="text-indigo-600 hover:text-indigo-900" data-id="element-5119">
                        <ExternalLinkIcon className="h-4 w-4" data-id="element-5120" />
                      </button>
                    </li>)}
                </ul> : <p className="mt-2 text-sm text-gray-500 italic" data-id="element-5121">
                  No child files
                </p>}
            </div>

            {/* Linked Entities */}
            <div data-id="element-5122">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-5123">
                Linked Entities
              </h4>
              {file.linkedEntities && file.linkedEntities.length > 0 ? <ul className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200" data-id="element-5124">
                  {file.linkedEntities.map(entity => <li key={entity.id} className="py-3 flex items-center justify-between text-sm" data-id="element-5125">
                      <div className="flex items-center" data-id="element-5126">
                        <LinkIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-5127" />
                        <span className="text-gray-900" data-id="element-5128">
                          {entity.name}{' '}
                          <span className="text-gray-500" data-id="element-5129">({entity.type})</span>
                        </span>
                      </div>
                      <button className="text-indigo-600 hover:text-indigo-900" data-id="element-5130">
                        <ExternalLinkIcon className="h-4 w-4" data-id="element-5131" />
                      </button>
                    </li>)}
                </ul> : <p className="mt-2 text-sm text-gray-500 italic" data-id="element-5132">
                  No linked entities
                </p>}
            </div>
          </div>}

        {activeTab === 'history' && <div data-id="element-5133">
            <h4 className="text-sm font-medium text-gray-900" data-id="element-5134">File History</h4>
            <ul className="mt-2 border-t border-gray-200 divide-y divide-gray-200" data-id="element-5135">
              <li className="py-3" data-id="element-5136">
                <div className="flex items-center text-sm" data-id="element-5137">
                  <ClockIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-5138" />
                  <p className="text-gray-900 font-medium" data-id="element-5139">File modified</p>
                </div>
                <div className="mt-1 flex items-center text-sm text-gray-500" data-id="element-5140">
                  <UserIcon className="h-3 w-3 text-gray-400 mr-1" data-id="element-5141" />
                  <span data-id="element-5142">{file.uploadedBy}</span>
                  <span className="mx-1" data-id="element-5143">•</span>
                  <span data-id="element-5144">{formatDateTime(file.lastModified, locale)}</span>
                </div>
              </li>
              <li className="py-3" data-id="element-5145">
                <div className="flex items-center text-sm" data-id="element-5146">
                  <ClockIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-5147" />
                  <p className="text-gray-900 font-medium" data-id="element-5148">File uploaded</p>
                </div>
                <div className="mt-1 flex items-center text-sm text-gray-500" data-id="element-5149">
                  <UserIcon className="h-3 w-3 text-gray-400 mr-1" data-id="element-5150" />
                  <span data-id="element-5151">{file.uploadedBy}</span>
                  <span className="mx-1" data-id="element-5152">•</span>
                  <span data-id="element-5153">{formatDateTime(file.uploadDate, locale)}</span>
                </div>
              </li>
            </ul>
          </div>}
      </div>

      {/* Action Buttons */}
      <div className="mt-6 border-t border-gray-200 pt-4 flex justify-between" data-id="element-5154">
        <div data-id="element-5155">
          <button onClick={() => onDownload(file.id)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-5156">
            <DownloadIcon className="h-4 w-4 mr-1" data-id="element-5157" />
            Download
          </button>
        </div>
        <div className="flex space-x-2" data-id="element-5158">
          <button onClick={() => onMove(file)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-5159">
            <FolderIcon className="h-4 w-4 mr-1" data-id="element-5160" />
            Move
          </button>
          <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-5161">
            <EditIcon className="h-4 w-4 mr-1" data-id="element-5162" />
            Edit
          </button>
        </div>
      </div>
    </div>;
}